<template>
  <div class="flex items-center gap-1 px-2">
    <Button
      text
      rounded
      size="small"
      :severity="activeFormats.bold ? 'primary' : 'secondary'"
      :aria-pressed="activeFormats.bold"
      :aria-label="t('whatsapp.formatting.bold')"
      v-tooltip.top="t('whatsapp.formatting.bold_shortcut')"
      :class="buttonClass(activeFormats.bold)"
      @mousedown.prevent
      @click="$emit('format-bold')"
    >
      <template #icon>
        <Bold :size="12" class="text-[var(--p-surface-500)] dark:text-[var(--p-surface-50)]" />
      </template>
    </Button>
    <Button
      text
      rounded
      size="small"
      :severity="activeFormats.italic ? 'primary' : 'secondary'"
      :aria-pressed="activeFormats.italic"
      :aria-label="t('whatsapp.formatting.italic')"
      v-tooltip.top="t('whatsapp.formatting.italic_shortcut')"
      :class="buttonClass(activeFormats.italic)"
      @mousedown.prevent
      @click="$emit('format-italic')"
    >
      <template #icon>
        <Italic :size="12" class="text-[var(--p-surface-500)] dark:text-[var(--p-surface-50)]" />
      </template>
    </Button>
    <Button
      text
      rounded
      size="small"
      :severity="activeFormats.strikethrough ? 'primary' : 'secondary'"
      :aria-pressed="activeFormats.strikethrough"
      :aria-label="t('whatsapp.formatting.strikethrough')"
      v-tooltip.top="t('whatsapp.formatting.strikethrough_shortcut')"
      :class="buttonClass(activeFormats.strikethrough)"
      @mousedown.prevent
      @click="$emit('format-strikethrough')"
    >
      <template #icon>
        <Strikethrough
          :size="12"
          class="text-[var(--p-surface-500)] dark:text-[var(--p-surface-50)]"
        />
      </template>
    </Button>
    <Button
      text
      rounded
      size="small"
      :severity="activeFormats.monospace ? 'primary' : 'secondary'"
      :aria-pressed="activeFormats.monospace"
      :aria-label="t('whatsapp.formatting.monospace')"
      v-tooltip.top="t('whatsapp.formatting.monospace_shortcut')"
      :class="buttonClass(activeFormats.monospace)"
      @mousedown.prevent
      @click="$emit('format-monospace')"
    >
      <template #icon>
        <Code :size="12" class="text-[var(--p-surface-500)] dark:text-[var(--p-surface-50)]" />
      </template>
    </Button>
  </div>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import { Bold, Italic, Strikethrough, Code } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import type { RichTextFormat } from '@/components/whatsapp/composables/useRichWhatsappEditor'

type ActiveFormats = Record<RichTextFormat, boolean>

interface Props {
  messageText: string
  activeFormats?: ActiveFormats
}

withDefaults(defineProps<Props>(), {
  activeFormats: () => ({
    bold: false,
    italic: false,
    strikethrough: false,
    monospace: false,
  }),
})

const { t } = useI18n()

const buttonClass = (isActive: boolean) => [
  'p-1! transition-all duration-150 hover:bg-[var(--p-primary-50)]! dark:hover:bg-[var(--p-primary-900)]/30!',
  isActive
    ? 'bg-[var(--p-primary-100)]! text-[var(--p-primary-color)]! ring-1 ring-[var(--p-primary-color)]/35 dark:bg-[var(--p-primary-900)]/30!'
    : '',
]

defineEmits<{
  'format-bold': []
  'format-italic': []
  'format-strikethrough': []
  'format-monospace': []
  'ai-insert': [text: string]
}>()
</script>
